import { CheckCircle2, HelpCircle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { useQuizAnalytics } from '@/services/admin/metrics.service'
import type { DashboardFilters } from './types'

interface Props {
  filters: DashboardFilters
}

/**
 * Nota média dos quizzes por módulo. Reusa `useQuizAnalytics` — mesma
 * fonte que o dashboard legado.
 */
export function QuizScoreByModule({ filters }: Props) {
  const { data, isLoading } = useQuizAnalytics(filters.courseId, filters.companyIds)

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  const modules = data?.averageScoreByModule ?? []

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="w-4 h-4 text-primary" />
          Nota média por módulo
        </CardTitle>
        <CardDescription>Score médio em quizzes por módulo</CardDescription>
      </CardHeader>
      <CardContent>
        {modules.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Sem quizzes respondidos no filtro.
          </p>
        ) : (
          <ul className="space-y-3">
            {modules.map((m) => (
              <li key={m.moduleId} className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-3 text-xs">
                  <span className="font-medium truncate flex-1">{m.moduleName}</span>
                  <span className="text-muted-foreground tabular-nums whitespace-nowrap">
                    {m.averageScore.toFixed(0)}% · {m.totalAttempts} tent.
                  </span>
                </div>
                <Progress value={m.averageScore} className="h-2" />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Tentativas por quiz — lista com avg/max/min score de cada quiz.
 * Substitui o bloco "Tentativas por Quiz" do dashboard legado.
 */
export function QuizAttemptsList({ filters }: Props) {
  const { data, isLoading } = useQuizAnalytics(filters.courseId, filters.companyIds)

  if (isLoading) {
    return (
      <Card className="glass-card border-border/50 h-full">
        <CardHeader>
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  const quizzes = (data?.attemptsByQuiz ?? []).slice(0, 8)

  return (
    <Card className="glass-card border-border/50 h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <HelpCircle className="w-4 h-4 text-primary" />
          Performance por quiz
        </CardTitle>
        <CardDescription>Avg · maior · menor pontuação · {quizzes.length} quizzes</CardDescription>
      </CardHeader>
      <CardContent>
        {quizzes.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nenhum quiz com tentativas no filtro.
          </p>
        ) : (
          <ul className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1">
            {quizzes.map((q) => (
              <li
                key={q.quizId}
                className="rounded-lg border border-border/40 p-3 hover:bg-card/30 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <p className="text-sm font-medium truncate flex-1">{q.quizTitle}</p>
                  <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                    {q.totalAttempts} tent.
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground truncate mb-2">
                  {q.moduleName} · {q.episodeTitle}
                </p>
                <div className="grid grid-cols-3 gap-1.5 text-xs">
                  <ScoreStat label="Avg" value={q.averageScore} color="text-primary" />
                  <ScoreStat label="Max" value={q.highestScore} color="text-emerald-400" />
                  <ScoreStat label="Min" value={q.lowestScore} color="text-rose-400" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

function ScoreStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded bg-muted/30 px-2 py-1 text-center">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${color}`}>{value.toFixed(0)}%</div>
    </div>
  )
}
